import type { ApiAccessClient, CommunityWorkflowConfiguration } from '@communities/content-management/domain';
import { WorkflowType } from '@communities/content-management/domain';
import { useCommunityWorkflows, useUpsertCommunityWorkflow } from '@communities/content-management/hooks';
import { DataList, SectionHeader, type DeleteItemContext, type TableColumn } from '@core/components';
import { Text, Badge, IconButton, Info16, IconButtonSize, IconSize, Button } from '@soteria/design-system-ve';
import { useCallback, useMemo, useState } from 'react';

import { ProviderApplicationDialog } from './ProviderApplicationDialog';
import { ProviderApplicationsMoreInfoDialog } from './ProviderApplicationsMoreInfoDialog';
import * as styles from './ProviderApplicationsSection.css';

interface ProviderApplicationsSectionProps {
  communityId: string;
  isCommunityAdmin: boolean;
}

/**
 * Builds the updated workflow configuration for a delete operation
 */
function buildDeleteWorkflow(targetWorkflowType: WorkflowType, targetWorkflow: CommunityWorkflowConfiguration | null, provider: ApiAccessClient) {
  const currentClients = targetWorkflow?.apiSettings?.accessClients || [];
  const updatedClients = currentClients
    .filter(p => p.providerUrl !== provider.providerUrl || JSON.stringify(p.clientIdList) !== JSON.stringify(provider.clientIdList))
    // Strip UI-only workflowType annotation so it never reaches the mutation payload
    .map(({ workflowType: _wt, ...client }) => client);

  return {
    type: targetWorkflowType,
    topicActions: targetWorkflow?.topicActions || [],
    apiSettings: {
      maxTps: targetWorkflow?.apiSettings?.maxTps || 100,
      accessClients: updatedClients,
    },
  };
}

/**
 * Displays API Client Applications from both classification and moderation workflows
 * in a unified table with a Workflow Type column.
 */
export function ProviderApplicationsSection({ communityId, isCommunityAdmin }: ProviderApplicationsSectionProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isMoreInfoDialogOpen, setIsMoreInfoDialogOpen] = useState(false);
  const [selectedProviderApplication, setSelectedProviderApplication] = useState<ApiAccessClient | null>(null);

  // Load both workflow types — Apollo deduplicates to a single network request
  const { workflows: classificationWorkflow, loading: classLoading, errors: classErrors } = useCommunityWorkflows(communityId, WorkflowType.CLASSIFICATION);
  const { workflows: moderationWorkflow, loading: modLoading, errors: modErrors } = useCommunityWorkflows(communityId, WorkflowType.MODERATION);
  const { upsertWorkflow, loading: isDeleting, errors: deleteErrors, resetErrors } = useUpsertCommunityWorkflow();

  const loading = classLoading || modLoading;
  const errors = useMemo(() => {
    if (classErrors && modErrors) return [...classErrors, ...modErrors];

    return classErrors || modErrors;
  }, [classErrors, modErrors]);

  // Merge providers from both workflows with workflowType annotations
  const accessClients: ApiAccessClient[] = useMemo(() => {
    const classClients = (classificationWorkflow?.apiSettings?.accessClients || []).map(client => ({
      ...client,
      workflowType: WorkflowType.CLASSIFICATION,
    }));
    const modClients = (moderationWorkflow?.apiSettings?.accessClients || []).map(client => ({
      ...client,
      workflowType: WorkflowType.MODERATION,
    }));

    return [...classClients, ...modClients];
  }, [classificationWorkflow, moderationWorkflow]);

  const handleOpenDialog = useCallback((provider?: ApiAccessClient) => {
    setSelectedProviderApplication(provider || null);
    setIsDialogOpen(true);
  }, []);

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedProviderApplication(null);
  };

  const handleDeleteProvider = async (provider: ApiAccessClient): Promise<boolean> => {
    const targetWorkflowType = provider.workflowType || WorkflowType.CLASSIFICATION;
    const targetWorkflow = targetWorkflowType === WorkflowType.CLASSIFICATION ? classificationWorkflow : moderationWorkflow;
    const updatedWorkflow = buildDeleteWorkflow(targetWorkflowType, targetWorkflow, provider);

    return await upsertWorkflow(communityId, targetWorkflowType, updatedWorkflow);
  };

  // Create the delete item context
  const deleteItemContext: DeleteItemContext<ApiAccessClient> = {
    handler: handleDeleteProvider,
    isDeleting,
    errors: deleteErrors,
    resetErrors,
  };

  // Define table columns for provider applications
  const columns: TableColumn<ApiAccessClient>[] = [
    {
      key: 'workflowType',
      header: 'Workflow Type',
      render: provider => <Text>{provider.workflowType === WorkflowType.CLASSIFICATION ? 'Classification' : 'Moderation'}</Text>,
      width: '15%',
    },
    {
      key: 'url',
      header: 'Provider URL',
      render: provider => <Text>{provider.providerUrl || 'No URL configured'}</Text>,
      width: '45%',
    },
    {
      key: 'clientIds',
      header: 'Client IDs',
      render: provider => (
        <div className={styles.badgeContainer}>
          {provider.clientIdList.slice(0, 3).map(clientId => (
            <Badge key={`${provider.providerUrl}-${clientId || 'empty'}`} text={clientId || 'Empty'} />
          ))}
          {provider.clientIdList.length > 3 && <Badge text={`+${provider.clientIdList.length - 3} more`} />}
        </div>
      ),
      width: '40%',
    },
  ];

  return (
    <div>
      <SectionHeader
        description="Manage OAuth2 client applications authorized to access your community's API endpoints."
        endComponent={
          <div className={styles.headerActions}>
            <IconButton
              aria-label="Learn more about API client applications"
              buttonSize={IconButtonSize.Medium}
              icon={Info16}
              iconSize={IconSize.XSmall}
              onClick={() => setIsMoreInfoDialogOpen(true)}
            />
            {isCommunityAdmin && <Button onClick={() => handleOpenDialog()}>ADD PROVIDER</Button>}
          </div>
        }
        title={`API Client Applications (${accessClients.length})`}
      />

      {/* Provider Applications List */}
      <DataList
        columns={columns}
        data={accessClients}
        dataTypeName="provider application"
        deleteItemContext={isCommunityAdmin ? deleteItemContext : undefined}
        error={errors}
        isLoading={loading}
        onEdit={isCommunityAdmin ? handleOpenDialog : undefined}
      />

      {/* Create/Edit Provider Application Dialog */}
      <ProviderApplicationDialog communityId={communityId} isOpen={isDialogOpen} provider={selectedProviderApplication} onClose={handleCloseDialog} />

      {/* More Info Dialog */}
      <ProviderApplicationsMoreInfoDialog isOpen={isMoreInfoDialogOpen} onClose={() => setIsMoreInfoDialogOpen(false)} />
    </div>
  );
}
